/**
 * EngineClient — the `Engine` interface over contracts/rpc.ts (web.md §1, §3).
 *
 * Every Engine method becomes `call(path, args)`: a request `{ id: '<tabId>:<n>', path, args }` sent
 * through the EngineLink; exactly one response settles it. Errors are rebuilt as DigesterError instances.
 * `subscribe` is served locally from the event stream.
 *
 * Robustness rules (rpc.ts "Invariants"):
 *   - No leader known yet → requests wait for the first `leader-ready`.
 *   - A remote leader that does not answer within RPC_TIMEOUT_MS, or a `leader-ready` from a different
 *     leader while a request is in flight → the request is sent again ONCE (same id: the host dedupes).
 *   - `host-error` (e.g. storage_unavailable) rejects everything in flight and is kept in the status.
 *   - A sync answer `sync_conflict` (the server speaks a newer data schema, models.md §9.5) sets
 *     `updateAvailable` for the rest of the page's life: the shell shows "A new version is available —
 *     reload" (`useEngineStatus().updateAvailable`). Every tab sees it: sync events are broadcast.
 */

import {
  DigesterError, METHOD_ACCESS, RPC_TIMEOUT_MS, fromShape,
  type DigesterErrorShape, type Engine, type EngineEvent, type RpcRequest,
} from '@digester/core';
import type { EngineLink, LinkInbound } from './link.js';

export interface EngineClientStatus {
  /** The tab whose worker currently serves the engine; null until the first leader-ready. */
  leaderId: string | null;
  /** False when the engine runs on an in-memory database (no OPFS); null until known. */
  durable: boolean | null;
  /** A fatal start-up failure of the engine host. */
  hostError: DigesterErrorShape | null;
  /** The server refused this bundle's data schema (`sync_conflict`): a newer version must be loaded. */
  updateAvailable: boolean;
}

export interface EngineClient {
  readonly engine: Engine;
  readonly tabId: string;
  status(): EngineClientStatus;
  onStatus(listener: (status: EngineClientStatus) => void): () => void;
  /** Detach from the link (does not close the shared engine). */
  dispose(): void;
}

export interface EngineClientOptions {
  /** Timeout for requests served by another tab. Default RPC_TIMEOUT_MS. */
  timeoutMs?: number;
}

interface Pending {
  request: RpcRequest;
  resolve(value: unknown): void;
  reject(error: DigesterError): void;
  /** Leader the request was last sent to; null = not sent yet. */
  sentTo: string | null;
  retried: boolean;
  timer: ReturnType<typeof setTimeout> | null;
}

type AnyFn = (...args: unknown[]) => unknown;

/** A sync status that says the server speaks another data schema (models.md §9.5). */
export function isSchemaConflict(event: EngineEvent): boolean {
  return event.type === 'sync' && event.status.phase === 'error' && event.status.error?.code === 'sync_conflict';
}

/** Build a nested object with one function per dotted path. */
function buildApi(paths: readonly string[], make: (path: string) => AnyFn): Record<string, unknown> {
  const root: Record<string, unknown> = {};
  for (const path of paths) {
    const parts = path.split('.');
    let node = root;
    for (let i = 0; i < parts.length - 1; i++) {
      const key = parts[i] as string;
      node[key] ??= {};
      node = node[key] as Record<string, unknown>;
    }
    node[parts[parts.length - 1] as string] = make(path);
  }
  return root;
}

export function createEngineClient(link: EngineLink, options: EngineClientOptions = {}): EngineClient {
  const timeoutMs = options.timeoutMs ?? RPC_TIMEOUT_MS;
  const pending = new Map<string, Pending>();
  const eventListeners = new Set<(event: EngineEvent) => void>();
  const statusListeners = new Set<(status: EngineClientStatus) => void>();
  let status: EngineClientStatus = { leaderId: null, durable: null, hostError: null, updateAvailable: false };
  let counter = 0;
  let disposed = false;

  const setStatus = (patch: Partial<EngineClientStatus>): void => {
    status = { ...status, ...patch };
    for (const listener of [...statusListeners]) listener(status);
  };

  const settle = (id: string): Pending | undefined => {
    const p = pending.get(id);
    if (!p) return undefined;
    pending.delete(id);
    if (p.timer) clearTimeout(p.timer);
    return p;
  };

  const transmit = (p: Pending): void => {
    if (p.timer) clearTimeout(p.timer);
    p.timer = null;
    if (!link.isLocal() && status.leaderId === null) {
      p.sentTo = null; // wait for a leader
      return;
    }
    p.sentTo = status.leaderId ?? link.tabId;
    link.send(p.request);
    if (!link.isLocal()) {
      p.timer = setTimeout(() => {
        if (!pending.has(p.request.id)) return;
        if (!p.retried) {
          p.retried = true;
          transmit(p);
        } else {
          settle(p.request.id)?.reject(new DigesterError('internal', 'The engine did not answer. Reload the page if this persists.', { path: p.request.path }));
        }
      }, timeoutMs);
    }
  };

  const onMessage = (message: LinkInbound): void => {
    switch (message.kind) {
      case 'response': {
        const p = settle(message.id);
        if (!p) return;
        if (message.ok) p.resolve(message.result);
        else p.reject(fromShape(message.error));
        return;
      }
      case 'event':
        if (!status.updateAvailable && isSchemaConflict(message.event)) setStatus({ updateAvailable: true });
        for (const listener of [...eventListeners]) {
          try {
            listener(message.event);
          } catch (error) {
            console.log(`[engine-client] an event listener threw: ${error instanceof Error ? error.message : String(error)}`);
          }
        }
        return;
      case 'leader-ready': {
        const changed = status.leaderId !== message.leaderId;
        setStatus({ leaderId: message.leaderId, durable: message.durable, hostError: null });
        if (!changed) {
          for (const p of pending.values()) if (p.sentTo === null) transmit(p);
          return;
        }
        for (const p of [...pending.values()]) {
          if (p.sentTo === null) transmit(p);
          else if (p.sentTo !== message.leaderId) {
            if (p.retried) {
              settle(p.request.id)?.reject(new DigesterError('internal', 'The engine host changed while this request was running. Try again.', { path: p.request.path }));
            } else {
              p.retried = true;
              transmit(p);
            }
          }
        }
        return;
      }
      case 'host-error': {
        setStatus({ hostError: message.error });
        const error = fromShape(message.error);
        for (const id of [...pending.keys()]) settle(id)?.reject(error);
        return;
      }
    }
  };

  const unsubscribe = link.subscribe(onMessage);

  const call = (path: string, args: unknown[]): Promise<unknown> => {
    if (disposed) return Promise.reject(new DigesterError('internal', 'The engine client was disposed'));
    if (status.hostError) return Promise.reject(fromShape(status.hostError));
    const request: RpcRequest = { kind: 'request', id: `${link.tabId}:${++counter}`, tabId: link.tabId, path, args };
    return new Promise((resolve, reject) => {
      const p: Pending = { request, resolve, reject, sentTo: null, retried: false, timer: null };
      pending.set(request.id, p);
      transmit(p);
    });
  };

  const paths = Object.keys(METHOD_ACCESS).filter((p) => p !== 'subscribe');
  const api = buildApi(paths, (path) => (...args: unknown[]) => call(path, args));
  api.subscribe = (listener: (event: EngineEvent) => void) => {
    eventListeners.add(listener);
    return () => {
      eventListeners.delete(listener);
    };
  };
  const engine = api as unknown as Engine;

  return {
    engine,
    tabId: link.tabId,
    status: () => status,
    onStatus(listener) {
      statusListeners.add(listener);
      return () => {
        statusListeners.delete(listener);
      };
    },
    dispose() {
      if (disposed) return;
      disposed = true;
      unsubscribe();
      const error = new DigesterError('internal', 'The engine client was disposed');
      for (const id of [...pending.keys()]) settle(id)?.reject(error);
      eventListeners.clear();
      statusListeners.clear();
    },
  };
}
